// src/pages/AdminUsersManage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Box, Typography, Card, CardContent, Grid, Chip, Button,
    TextField, InputAdornment, Dialog, DialogTitle, DialogContent,
    DialogActions, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, IconButton, Tooltip, Avatar, CircularProgress, Alert,
    Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
    Snackbar, useMediaQuery, useTheme as useMuiTheme
} from '@mui/material';
import {
    Search, Block, CheckCircle, Person, FitnessCenter,
    Email, Visibility, Warning, Restaurant, CardGiftcard, Close,
    AccessTime, RemoveCircle
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import AdminHeader from '../components/AdminHeader';
import axios from 'axios';

const AdminUsersManage = () => {
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const { isDarkMode } = useTheme();
    const muiTheme = useMuiTheme();
    const navigate = useNavigate();

    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(muiTheme.breakpoints.between('sm', 'md'));

    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [allPrograms, setAllPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);

    const [banDialog, setBanDialog] = useState(false);
    const [detailsDialog, setDetailsDialog] = useState(false);
    const [grantProgramDialog, setGrantProgramDialog] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [error, setError] = useState('');
    const [grantLoading, setGrantLoading] = useState(false);
    const [revokeLoading, setRevokeLoading] = useState(null); // programId кој се брише

    const token = localStorage.getItem('token');
    const API = process.env.REACT_APP_API_URL;

    useEffect(() => {
        if (!authLoading && (!isAuthenticated || user?.role !== 'ADMIN')) {
            navigate('/login');
        }
    }, [isAuthenticated, user, authLoading, navigate]);

    useEffect(() => { fetchUsers(); fetchAllPrograms(); }, []);

    useEffect(() => {
        if (searchTerm === '') {
            setFilteredUsers(users);
        } else {
            const filtered = users.filter(u => {
                const fullName = u.firstName && u.lastName
                    ? `${u.firstName} ${u.lastName}`.toLowerCase()
                    : (u.name || '').toLowerCase();
                return fullName.includes(searchTerm.toLowerCase()) ||
                    u.email?.toLowerCase().includes(searchTerm.toLowerCase());
            });
            setFilteredUsers(filtered);
        }
    }, [searchTerm, users]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API}/admin/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(res.data);
            setFilteredUsers(res.data);
        } catch (err) {
            setError('Грешка при вчитување на корисници');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllPrograms = async () => {
        try {
            const res = await axios.get(`${API}/admin/programs`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAllPrograms(res.data || []);
        } catch (err) {
            console.error('Грешка при вчитување на програми:', err);
        }
    };

    const handleBanUser = async () => {
        try {
            await axios.put(
                `${API}/admin/users/${selectedUser.id}/ban`, {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSnackbar({
                open: true,
                message: `Корисникот е успешно ${selectedUser.banned ? 'одбаниран' : 'баниран'}!`,
                severity: 'success'
            });
            setBanDialog(false);
            fetchUsers();
        } catch (err) {
            setSnackbar({ open: true, message: 'Грешка при банирање', severity: 'error' });
        }
    };

    const handleGrantProgram = async (program) => {
        setGrantLoading(true);
        try {
            await axios.post(
                `${API}/admin/users/${selectedUser.id}/grant-program/${program.id}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSnackbar({
                open: true,
                message: `✅ ${selectedUser.firstName} доби пристап до „${program.name}"`,
                severity: 'success'
            });
            await fetchUsers();
            // Ажурирај го selectedUser со новите податоци
            const updated = users.find(u => u.id === selectedUser.id);
            if (updated) setSelectedUser(updated);
        } catch (err) {
            const errMsg = err.response?.data?.error || 'Грешка при давање пристап';
            setSnackbar({ open: true, message: `❌ ${errMsg}`, severity: 'error' });
        } finally {
            setGrantLoading(false);
        }
    };

    // ✅ НОВО - Одземи програма
    const handleRevokeProgram = async (program) => {
        setRevokeLoading(program.id);
        try {
            await axios.delete(
                `${API}/admin/users/${selectedUser.id}/revoke-program/${program.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSnackbar({
                open: true,
                message: `✅ Одземен пристап до „${program.name}" за ${selectedUser.firstName}`,
                severity: 'success'
            });
            await fetchUsers();
            const updated = users.find(u => u.id === selectedUser.id);
            if (updated) setSelectedUser(updated);
        } catch (err) {
            const errMsg = err.response?.data?.error || 'Грешка при одземање пристап';
            setSnackbar({ open: true, message: `❌ ${errMsg}`, severity: 'error' });
        } finally {
            setRevokeLoading(null);
        }
    };

    const openGrantDialog = (u) => {
        setSelectedUser(u);
        setGrantProgramDialog(true);
    };

    const calculateDaysLeft = (expiryDate) => {
        if (!expiryDate) return 0;
        const diffDays = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    };

    const getUserPrograms = (u) => u?.purchasedPrograms || [];

    if (authLoading || loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress sx={{ color: 'primary.main' }} size={60} />
            </Box>
        );
    }

    return (
        <Box sx={{ py: { xs: 2, md: 4 }, minHeight: '100vh', backgroundColor: 'background.default' }}>
            <Container maxWidth="xl">
                <AdminHeader />

                <Box sx={{ mb: 4, textAlign: 'center' }}>
                    <Typography variant={isMobile ? 'h4' : 'h2'} sx={{
                        color: 'primary.main', fontWeight: 'bold', mb: 2,
                        textShadow: isDarkMode ? '0 0 20px rgba(255, 126, 185, 0.5)' : 'none'
                    }}>
                        👥 Управување со корисници
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                        Преглед и управување со сите регистрирани корисници
                    </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

                {/* Stats Cards */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    {[
                        { value: users.length, label: 'Вкупно', color: '#4CAF50', icon: <Person sx={{ fontSize: { xs: 35, md: 50 } }} /> },
                        { value: users.filter(u => !u.banned).length, label: 'Активни', color: '#2196F3', icon: <CheckCircle sx={{ fontSize: { xs: 35, md: 50 } }} /> },
                        { value: users.filter(u => u.banned).length, label: 'Банирани', color: '#ff6b6b', icon: <Block sx={{ fontSize: { xs: 35, md: 50 } }} /> },
                        { value: users.reduce((s, u) => s + (u.purchasedPrograms?.length || 0), 0), label: 'Програми', color: '#ff7eb9', icon: <FitnessCenter sx={{ fontSize: { xs: 35, md: 50 } }} /> },
                        { value: users.reduce((s, u) => s + (u.purchasedDiets?.length || 0), 0), label: 'Диети', color: '#FFA726', icon: <Restaurant sx={{ fontSize: { xs: 35, md: 50 } }} /> },
                    ].map((stat, i) => (
                        <Grid item xs={6} sm={4} md={2.4} key={i}>
                            <Card sx={{ bgcolor: 'background.paper', border: `2px solid ${stat.color}`, '&:hover': { transform: 'translateY(-4px)', transition: 'all 0.3s' } }}>
                                <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Box>
                                            <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ color: stat.color, fontWeight: 'bold' }}>{stat.value}</Typography>
                                            <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                                        </Box>
                                        <Box sx={{ color: stat.color }}>{stat.icon}</Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Search */}
                <Card sx={{ mb: 3, bgcolor: 'background.paper' }}>
                    <CardContent>
                        <TextField
                            fullWidth
                            placeholder="Пребарај по ime или емаил..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size={isMobile ? 'small' : 'medium'}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><Search sx={{ color: 'primary.main' }} /></InputAdornment>
                            }}
                        />
                    </CardContent>
                </Card>

                {/* Table */}
                <TableContainer component={Paper} sx={{ bgcolor: 'background.paper' }}>
                    <Table size={isMobile ? 'small' : 'medium'}>
                        <TableHead sx={{ bgcolor: isDarkMode ? 'rgba(255, 126, 185, 0.1)' : 'rgba(255, 126, 185, 0.05)' }}>
                            <TableRow>
                                <TableCell><strong>Корисник</strong></TableCell>
                                {!isMobile && <TableCell><strong>Емаил</strong></TableCell>}
                                <TableCell align="center"><strong>Прог.</strong></TableCell>
                                <TableCell align="center"><strong>Статус</strong></TableCell>
                                <TableCell align="center"><strong>Акции</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUsers.map((u) => (
                                <TableRow key={u.id} sx={{ '&:hover': { bgcolor: isDarkMode ? 'rgba(255, 126, 185, 0.05)' : 'rgba(255, 126, 185, 0.02)' } }}>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Avatar sx={{ bgcolor: 'primary.main', width: isMobile ? 30 : 40, height: isMobile ? 30 : 40, fontSize: isMobile ? '0.8rem' : '1rem' }}>
                                                {u.firstName?.charAt(0).toUpperCase() || 'U'}
                                            </Avatar>
                                            <Box>
                                                <Typography variant={isMobile ? 'caption' : 'body1'} fontWeight="bold" display="block">
                                                    {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email}
                                                </Typography>
                                                {isMobile && (
                                                    <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    {!isMobile && (
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Email fontSize="small" color="action" />
                                                <Typography variant="body2">{u.email}</Typography>
                                            </Box>
                                        </TableCell>
                                    )}
                                    <TableCell align="center">
                                        <Chip
                                            icon={<FitnessCenter sx={{ fontSize: isMobile ? '12px !important' : '16px !important' }} />}
                                            label={u.purchasedPrograms?.length || 0}
                                            size={isMobile ? 'small' : 'medium'}
                                            sx={{ bgcolor: '#ff7eb9', color: 'white', fontWeight: 'bold' }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={u.banned ? 'Баниран' : 'Активен'}
                                            color={u.banned ? 'error' : 'success'}
                                            size={isMobile ? 'small' : 'medium'}
                                            icon={u.banned ? <Block /> : <CheckCircle />}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box display="flex" gap={0.5} justifyContent="center" flexWrap="wrap">
                                            <Tooltip title="Детали">
                                                <IconButton size={isMobile ? 'small' : 'medium'} onClick={() => { setSelectedUser(u); setDetailsDialog(true); }} sx={{ color: '#2196F3' }}>
                                                    <Visibility fontSize={isMobile ? 'small' : 'medium'} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Програма">
                                                <IconButton
                                                    size={isMobile ? 'small' : 'medium'}
                                                    onClick={() => openGrantDialog(u)}
                                                    sx={{ color: 'white', bgcolor: '#ff7eb9', borderRadius: 2, px: isMobile ? 0.8 : 1.5, '&:hover': { bgcolor: '#ff4a97' } }}
                                                >
                                                    <CardGiftcard fontSize={isMobile ? 'small' : 'medium'} sx={{ mr: isMobile ? 0 : 0.5 }} />
                                                    {!isMobile && (
                                                        <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                                                            Програма
                                                        </Typography>
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={u.banned ? 'Одбанирај' : 'Банирај'}>
                                                <IconButton size={isMobile ? 'small' : 'medium'} onClick={() => { setSelectedUser(u); setBanDialog(true); }} sx={{ color: u.banned ? '#4CAF50' : '#ff6b6b' }}>
                                                    <Block fontSize={isMobile ? 'small' : 'medium'} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {filteredUsers.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="text.secondary">Нема пронајдено корисници</Typography>
                    </Box>
                )}
            </Container>

            {/* Dialog за програма */}
            <Dialog
                open={grantProgramDialog}
                onClose={() => setGrantProgramDialog(false)}
                maxWidth="sm" fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle sx={{
                    bgcolor: '#ff7eb9', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    py: 1.5
                }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <CardGiftcard />
                        <Typography fontWeight="bold" fontSize={isMobile ? '1rem' : '1.2rem'}>
                            Додај програма на корисникот
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setGrantProgramDialog(false)} sx={{ color: 'white' }}>
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}>
                    {/* Корисник инфо + тековни програми */}
                    {selectedUser && (
                        <Box sx={{
                            px: { xs: 2, md: 3 }, pt: 2, pb: 1.5,
                            bgcolor: isDarkMode ? 'rgba(255,126,185,0.08)' : 'rgba(255,126,185,0.05)',
                            borderBottom: '1px solid', borderColor: 'divider'
                        }}>
                            <Typography variant="body2" color="text.secondary">Корисник:</Typography>
                            <Typography variant="h6" fontWeight="bold" color="primary.main">
                                {selectedUser.firstName} {selectedUser.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{selectedUser.email}</Typography>

                            {/* Тековни програми со Оддземи копче */}
                            {getUserPrograms(selectedUser).length > 0 ? (
                                <Box>
                                    <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, color: 'text.primary' }}>
                                        💪 Тековни програми:
                                    </Typography>
                                    <Box display="flex" flexDirection="column" gap={0.8}>
                                        {getUserPrograms(selectedUser).map((p, i) => {
                                            const daysLeft = p.subscriptionExpiryDate
                                                ? calculateDaysLeft(p.subscriptionExpiryDate)
                                                : (p.remainingDays ?? 'N/A');
                                            const isExpired = daysLeft === 0;
                                            const isRevoking = revokeLoading === p.id;
                                            return (
                                                <Box key={i} display="flex" alignItems="center" gap={1} sx={{
                                                    px: 1.5, py: 0.8,
                                                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                                    borderRadius: 2,
                                                    border: `1px solid ${isExpired ? '#f44336' : '#4CAF50'}30`
                                                }}>
                                                    <FitnessCenter sx={{ fontSize: 18, color: '#ff7eb9', flexShrink: 0 }} />
                                                    <Typography variant="body2" fontWeight="bold" sx={{ flex: 1, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                                                        {p.name || `Програма ${i + 1}`}
                                                    </Typography>
                                                    <Chip
                                                        size="small"
                                                        icon={<AccessTime sx={{ fontSize: '14px !important' }} />}
                                                        label={isExpired ? 'Истечена' : `${daysLeft} дена`}
                                                        sx={{
                                                            bgcolor: isExpired ? '#f4433620' : '#4CAF5020',
                                                            color: isExpired ? '#f44336' : '#4CAF50',
                                                            fontWeight: 'bold',
                                                            fontSize: '0.65rem',
                                                            border: `1px solid ${isExpired ? '#f44336' : '#4CAF50'}50`,
                                                            flexShrink: 0
                                                        }}
                                                    />
                                                    {/* ✅ НОВО - Оддземи копче */}
                                                    <Tooltip title="Оддземи пристап">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRevokeProgram(p)}
                                                            disabled={isRevoking}
                                                            sx={{
                                                                color: '#ff6b6b',
                                                                p: 0.5,
                                                                flexShrink: 0,
                                                                '&:hover': { bgcolor: 'rgba(255,107,107,0.1)' }
                                                            }}
                                                        >
                                                            {isRevoking
                                                                ? <CircularProgress size={16} sx={{ color: '#ff6b6b' }} />
                                                                : <RemoveCircle fontSize="small" />
                                                            }
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    Корисникот нема активни програми
                                </Typography>
                            )}
                        </Box>
                    )}

                    <Box sx={{ px: 2, pt: 1.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ px: 1, pt: 0.5, pb: 0.5 }}>
                            Избери нова програма за да му дадеш пристап:
                        </Typography>
                    </Box>

                    {allPrograms.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">Нема програми во базата</Typography>
                        </Box>
                    ) : (
                        <List sx={{ pt: 0 }}>
                            {allPrograms.map((program) => {
                                const alreadyHas = getUserPrograms(selectedUser).some(
                                    p => p.id === program.id || p.name === program.name
                                );
                                return (
                                    <ListItem key={program.id} disablePadding divider>
                                        <ListItemButton
                                            onClick={() => !alreadyHas && handleGrantProgram(program)}
                                            disabled={grantLoading || alreadyHas}
                                            sx={{
                                                py: { xs: 1.5, md: 2 }, px: { xs: 2, md: 3 },
                                                opacity: alreadyHas ? 0.6 : 1,
                                                '&:hover': { bgcolor: alreadyHas ? 'transparent' : (isDarkMode ? 'rgba(255,126,185,0.1)' : 'rgba(255,126,185,0.05)') }
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: isMobile ? 40 : 56 }}>
                                                <Box sx={{
                                                    width: isMobile ? 36 : 44, height: isMobile ? 36 : 44,
                                                    borderRadius: 2,
                                                    bgcolor: alreadyHas ? 'rgba(76,175,80,0.15)' : 'rgba(255,126,185,0.15)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                }}>
                                                    <FitnessCenter sx={{ color: alreadyHas ? '#4CAF50' : '#ff7eb9', fontSize: isMobile ? 18 : 24 }} />
                                                </Box>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                        <Typography fontWeight="bold" fontSize={isMobile ? '0.85rem' : '1rem'}>
                                                            {program.name}
                                                        </Typography>
                                                        {alreadyHas && (
                                                            <Chip size="small" label="Веќе има" sx={{ bgcolor: '#4CAF5020', color: '#4CAF50', fontSize: '0.65rem', fontWeight: 'bold' }} />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                                                        <Chip size="small" label={`${program.price} ден`} sx={{ bgcolor: '#ff7eb9', color: 'white', fontSize: '0.65rem' }} />
                                                        <Chip size="small" label={`${program.durationDays} денови`} variant="outlined" sx={{ fontSize: '0.65rem' }} />
                                                    </Box>
                                                }
                                            />
                                            {!alreadyHas && (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    disabled={grantLoading}
                                                    sx={{
                                                        bgcolor: '#ff7eb9', color: 'white',
                                                        ml: 1, minWidth: isMobile ? 70 : 90,
                                                        fontSize: isMobile ? '0.7rem' : '0.8rem',
                                                        '&:hover': { bgcolor: '#ff4a97' }
                                                    }}
                                                >
                                                    {grantLoading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : '✅ Додај'}
                                                </Button>
                                            )}
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setGrantProgramDialog(false)} variant="outlined" fullWidth={isMobile}>
                        Откажи
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Ban Dialog */}
            <Dialog open={banDialog} onClose={() => setBanDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: selectedUser?.banned ? '#4CAF50' : '#ff6b6b', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning />
                    {selectedUser?.banned ? 'Одбанирај корисник' : 'Банирај корисник'}
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Дали сте сигурни дека сакате да го {selectedUser?.banned ? 'одбанирате' : 'банирате'} корисникот:
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                        {selectedUser?.firstName} {selectedUser?.lastName} ({selectedUser?.email})
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setBanDialog(false)} variant="outlined">Откажи</Button>
                    <Button onClick={handleBanUser} variant="contained" sx={{ bgcolor: selectedUser?.banned ? '#4CAF50' : '#ff6b6b' }}>
                        {selectedUser?.banned ? 'Одбанирај' : 'Банирај'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Details Dialog */}
            <Dialog open={detailsDialog} onClose={() => setDetailsDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Детали за корисник
                    {isMobile && <IconButton onClick={() => setDetailsDialog(false)} sx={{ color: 'white' }}><Close /></IconButton>}
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {selectedUser && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ color: 'primary.main' }}>📋 Основни информации</Typography>
                                <Box sx={{ pl: 2, mt: 1 }}>
                                    <Typography><strong>Ime:</strong> {selectedUser.firstName} {selectedUser.lastName}</Typography>
                                    <Typography><strong>Емаил:</strong> {selectedUser.email}</Typography>
                                    <Typography><strong>Улога:</strong> {selectedUser.role}</Typography>
                                    <Typography><strong>Статус:</strong> <Chip size="small" label={selectedUser.banned ? 'Баниран' : 'Активен'} color={selectedUser.banned ? 'error' : 'success'} /></Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12}><Divider /></Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" sx={{ color: '#ff7eb9' }}>💪 Купени програми</Typography>
                                {selectedUser.purchasedPrograms?.length > 0 ? (
                                    <Box sx={{ pl: 2, mt: 1 }}>
                                        {selectedUser.purchasedPrograms.map((p, i) => (
                                            <Chip key={i} icon={<FitnessCenter />} label={p.name || `Програма ${i + 1}`} sx={{ m: 0.5, bgcolor: '#ff7eb9', color: 'white' }} />
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography sx={{ pl: 2, mt: 1 }} color="text.secondary">Нема купени програми</Typography>
                                )}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" sx={{ color: '#FFA726' }}>🍽️ Купени диети</Typography>
                                {selectedUser.purchasedDiets?.length > 0 ? (
                                    <Box sx={{ pl: 2, mt: 1 }}>
                                        {selectedUser.purchasedDiets.map((d, i) => (
                                            <Chip key={i} icon={<Restaurant />} label={d.name || `Диета ${i + 1}`} sx={{ m: 0.5, bgcolor: '#FFA726', color: 'white' }} />
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography sx={{ pl: 2, mt: 1 }} color="text.secondary">Нема купени диети</Typography>
                                )}
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDetailsDialog(false)} variant="contained" fullWidth={isMobile}>Затвори</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 3, fontWeight: 'bold' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdminUsersManage;